import React, { useState, useContext, useEffect } from "react";
import { Button, Modal, Form as AntForm, Menu, Dropdown } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuOutlined, CloseOutlined } from "@ant-design/icons";
import { BiSearch } from "react-icons/bi";
import { AuthContext } from "contexts";
import { useAccess } from "contexts/access";
import { usePortal, clearPortalMode, switchItems } from "contexts/portal";
import Form from "components/Form";
import { instance } from "utils/axios";
import ls from "utils/ls";
import menu, { filterMenu, keyForPath } from "../../menu";
import logo from "../../../assets/images/logo.png";
import CommandPalette from "./CommandPalette";
import NotificationBell from "./NotificationBell";

// Эрхээр шүүсэн цэс → antd Menu items (perm/to-г хасна). top=true бол бүлэг нь доош унждаг цэс болно.
// Дээд цэсэнд бүлгийн нэр дээр дарахад эхний цэс рүү орно (go — navigate)
const toItems = (items, top, go) => items.map(({ perm, to, ...it }) => (it.children
  ? (top ? { key: it.key, label: it.label, children: it.children.map(({ perm: p, to: t, ...c }) => c), popupClassName: "top-nav-popup", onTitleClick: () => go && it.children[0]?.to && go(it.children[0].to) } : { ...it, children: it.children.map(({ perm: p, to: t, ...c }) => c) })
  : it));

// Нимгэн толгой: ⌘K хайлт, хэрэглэгч (рольууд), утсан дээр цэс
const Header = () => {
  const menuMode = "top";
  const location = useLocation();
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [palette, setPalette] = useState(false);
  const { state, action } = useContext(AuthContext);
  const { can, roles } = useAccess();
  const portal = usePortal();
  const [form] = AntForm.useForm();
  const navigate = useNavigate();
  const profile = state.userInfo?.profile;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = () => {
    clearPortalMode();
    action.logout();
    ls.remove("userInfo");
    navigate("/login");
  };

  const fields = [
    { label: "Хуучин нууц үг", name: "oldpassword", type: "password", className: "col-span-12 mb-0", rules: [{ required: true, message: "Хуучин нууц үг оруулна уу" }] },
    { label: "Шинэ нууц үг", name: "newpassword", type: "password", className: "col-span-12 mb-0", rules: [{ required: true, message: "Шинэ нууц үг оруулна уу" }] },
    { label: "Шинэ нууц үг давтах", name: "confirmpassword", type: "password", className: "col-span-12 mb-0", rules: [{ required: true, message: "Шинэ нууц үг давтан оруулна уу" }] },
  ];

  const handleSubmitChangePassword = (values) => {
    setLoading(true);
    instance({ method: "put", url: "changepassword", data: { oldpassword: values.oldpassword, newpassword: values.newpassword } })
      .then(() => setOpenModal(false))
      .catch(() => {})
      .then(() => setLoading(false));
  };

  const userMenu = {
    items: [
      { key: "me", label: "Хувийн мэдээлэл", onClick: () => navigate("/me") },
      ...switchItems(portal, navigate),
      { key: "mobile", label: "📱 Мобайл горим", onClick: () => { ls.remove("forceDesktop"); navigate("/m"); } },
      { key: "password", label: "Нууц үг солих", onClick: () => setOpenModal(true) },
      { type: "divider" },
      { key: "logout", label: "Гарах", onClick: handleLogout },
    ],
  };
  const initials = `${profile?.lastName?.[0] || ""}${profile?.firstName?.[0] || ""}`.toUpperCase();

  return (
    <>
      <header className="admin-top">
        {menuMode === "top" && (
          <>
            <a href="/" className="brand-logo-box shrink-0" aria-label="Нүүр"><img src={logo} width={104} alt="Tomujin Alternative School" /></a>
            <Menu mode="horizontal" className="top-nav flex-1 min-w-0" selectedKeys={[keyForPath(location.pathname)]} items={toItems(filterMenu(menu, can), true, navigate)} />
          </>
        )}
        <Button className="md:!hidden" icon={isOpenMenu ? <CloseOutlined /> : <MenuOutlined />} aria-label="Цэс" onClick={() => setIsOpenMenu(!isOpenMenu)} />
        <button type="button" onClick={() => setPalette(true)}
          className={`flex items-center gap-2.5 h-9 ${menuMode === "top" ? "w-9 justify-center shrink-0" : "w-[380px] max-w-full"} min-w-0 px-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-[13.5px] cursor-pointer hover:border-gray-300`} aria-label="Хайх (⌘K)">
          <BiSearch size={16} aria-hidden="true" />
          {menuMode !== "top" && <>
            <span className="truncate">Сурагч, эцэг эх, хичээл, цэс хайх</span>
            <kbd className="ml-auto hidden md:inline text-[11px] border border-gray-300 rounded px-1.5 bg-white font-sans">⌘K</kbd>
          </>}
        </button>
        <NotificationBell />
        <Dropdown menu={userMenu} trigger={["click"]} placement="bottomRight">
          <button type="button" className="flex items-center gap-2.5 bg-transparent border-0 cursor-pointer">
            <span className="text-right hidden sm:block leading-tight">
              <span className="block text-sm text-gray-900">{profile?.lastName?.[0]}. {profile?.firstName}</span>
              <span className="block text-xs text-gray-500">{/* Зөвхөн энэ (удирдлагын) горимын роль — багш/зөвлөх рольууд өөрсдийн хэсэгт */}
                {roles.filter((r) => !["teacher", "homeroom", "elementary", "house", "counselor"].includes(r.key)).map((r) => r.name).join(" · ") || profile?.role}</span>
            </span>
            <span className="w-8 h-8 rounded-full bg-brand-100 text-brand-800 flex items-center justify-center text-xs font-bold">{initials}</span>
          </button>
        </Dropdown>
      </header>
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      <Modal title={<div className="text-lg">Нууц үг солих</div>} open={openModal} onCancel={() => setOpenModal(false)} width={700} destroyOnClose footer={false}>
        <Form form={form} fields={fields} layout="vertical" onFinish={handleSubmitChangePassword} className="gap-4" />
        <div className="flex justify-end mt-4">
          <Button type="primary" onClick={() => form.submit()} loading={loading}>Хадгалах</Button>
        </div>
      </Modal>
      <div className={`fixed pb-12 w-full h-full flex flex-row z-[45] top-14 transition-all ${isOpenMenu ? "translate-x-0" : "translate-x-full"}`}>
        <div className="w-1/4" onClick={() => setIsOpenMenu(false)} />
        <div className="w-3/4 overflow-y-scroll bg-white border-l border-gray-200">
          <Menu onClick={() => setIsOpenMenu(false)} mode="inline" items={toItems(filterMenu(menu, can), false)} />
        </div>
      </div>
    </>
  );
};

export default Header;
